import { ChangeEvent, useRef } from 'react';
import { toast as sonnerToast } from 'sonner';

import { Button } from './ui';

import styles from './BugReportModal.module.css';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const;
type AllowedMime = (typeof ALLOWED_MIMES)[number];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export interface BugReportImagePickerProps {
  file: File | null;
  previewUrl: string | null;
  onChange: (file: File | null) => void;
}

export function BugReportImagePicker({ file, previewUrl, onChange }: BugReportImagePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files?.[0] ?? null;
    if (!next) return;
    if (!ALLOWED_MIMES.includes(next.type as AllowedMime)) {
      sonnerToast.error('Formato inválido. Use PNG, JPEG ou WebP.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (next.size > MAX_IMAGE_BYTES) {
      sonnerToast.error(`Imagem muito grande (${formatBytes(next.size)}). Máximo 5 MB.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    onChange(next);
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={styles.section}>
      <label htmlFor="bug-image" className={styles.label}>
        Imagem (opcional)
      </label>
      <div className={styles.fileRow}>
        <input
          ref={inputRef}
          id="bug-image"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileChange}
          className={styles.fileInput}
          aria-label="Anexar imagem do bug (PNG, JPEG ou WebP, máximo 5 MB)"
        />
      </div>
      {file && previewUrl && (
        <div className={styles.previewBlock}>
          <img src={previewUrl} alt="Pré-visualização" className={styles.previewImage} />
          <div className={styles.previewMeta}>
            <span className={styles.previewName}>
              {file.name} — {formatBytes(file.size)}
            </span>
            <Button variant="ghost" size="sm" onClick={handleRemove} type="button">
              Remover
            </Button>
          </div>
        </div>
      )}
      <span className={styles.hint}>PNG, JPEG ou WebP. Máximo 5 MB.</span>
    </div>
  );
}
